<template>
  <SchoolDetailSkeleton v-if="props.loading" />

  <template v-else-if="props.schoolData">
    <!-- Contenu détail école -->
    <!-- ── HERO BANNER avec IMAGE + LOGO ─────────────────────────────────────────────── -->
    <PrepeersHeroCover
      :src="schoolData?.schooldatas.coverPict"
      alt="Écoles PrePeers">
      <div class="hero-banner__logo">
        <PrepeersEntityLogo
          :logo-pict="schoolData?.schooldatas.logoPict"
          :entity-name="schoolData?.schooldatas.name ?? schoolData?.schooldatas.h1 ?? ''"
          :alt="`${schoolData?.schooldatas.name ?? 'École'} logo`" />
      </div>
    </PrepeersHeroCover>

    <PrepeersBreadcrumb
      auto
      section="ecoles"
      :current-label="schoolData?.schooldatas.h1" />

    <!-- ── HERO + H1 ─────────────────────────────────────────────── -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-section__h1">{{ schoolData?.schooldatas.h1 }}</h1>
        <div
          v-if="schoolData?.parsedSchoolTypeBlock"
          class="hero-section__subcontainer">
          <h2
            class="hero-section__title"
            v-html="schoolData?.parsedSchoolTypeBlock.Title"></h2>
          <div
            v-if="filterSeoLinksWithLabel(schoolData?.parsedSchoolTypeBlock.Links).length"
            class="hero-section__nav">
            <NuxtLink
              v-for="sl in filterSeoLinksWithLabel(schoolData?.parsedSchoolTypeBlock.Links)"
              :key="sl.Url"
              :to="localePath(sl.Url)"
              class="level-nav__link"
              >{{ sl.Text }}</NuxtLink
            >
          </div>
        </div>
        <div
          class="hero-section__intro"
          v-html="formatSeoIntro(schoolData?.schooldatas.introduction ?? '')" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── LIENS PAGE PARENT ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.parsedParentBlock"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ schoolData?.parsedParentBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in schoolData?.parsedParentBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── CTA SI NON CONNECTÉ ─────────────────────────────────────────────── -->
    <section
      v-if="!isLoggedIn"
      class="cta-section">
      <div class="container">
        <PrePeersLolaCta
          variant="ecole"
          :entity-name="schoolData?.schooldatas.name ?? schoolData?.schooldatas.h1 ?? ''" />
      </div>
    </section>

    <!-- ── LIENS PAGE PAYS ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.parsedCountriesBlock?.Links?.length"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ schoolData?.parsedCountriesBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in schoolData?.parsedCountriesBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── LIENS PAGE VILLES ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.parsedCitiesBlock?.Links?.length"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ schoolData?.parsedCitiesBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in schoolData?.parsedCitiesBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── TITRE VIDÉO + VIDEO ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.schooldatas.youTubeUrl"
      class="video-section">
      <div class="container">
        <h2 class="video-section__title">
          {{ schoolData?.schooldatas.h1 }} : {{ t('seointer.videoTextSuffix') }}
        </h2>
        <div class="video-section__wrapper">
          <iframe
            :src="schoolData?.schooldatas.youTubeUrl"
            title="Vidéo explicative"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            allowfullscreen
            class="video-section__iframe" />
        </div>
      </div>
    </section>

    <!-- ── LIENS PAGE FORMATIONS ─────────────────────────────────────────────── -->
    <section class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ schoolData?.parsedProgramsBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in schoolData?.parsedProgramsBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link btn-medium"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── CONTENU HTML RICHE ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.schooldatas.contentText"
      class="content-section">
      <div class="container">
        <h2 class="content-section__title">
          {{ t('main.about') }} {{ schoolData?.schooldatas.name }}
        </h2>
        <UiPrepeersQlEditorContent :html="schoolData?.schooldatas.contentText ?? ''" />
      </div>
    </section>

    <!-- ── LIENS PAGE DIPLOMES ─────────────────────────────────────────────── -->
    <section class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ schoolData?.parsedDegreesBlock.Title }}</h2>
        <div class="level-nav__list">
          <NuxtLink
            v-for="sl in schoolData?.parsedDegreesBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            class="level-nav__link"
            >{{ sl.Text }}</NuxtLink
          >
        </div>
      </div>
    </section>

    <!-- ── PRÉSENTATION EN CHIFFRES ─────────────────────────────────────────────── -->
    <UiPrePeersMetricsGrid :block="schoolData?.parsedMetricsBlock" />

    <!-- ── POURQUOI CHOISIR L'ÉCOLE ─────────────────────────────────────────────── -->
    <PrePeersHighlightsSection
      :title="t('seointer.root.title_differentiators', { name: schoolData?.schooldatas.name })"
      :highlights="schoolData?.parsedDifferentiators ?? []" />

    <!-- ── LE MOT DU DIRECTEUR ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.parsedDirectorBlock?.Content"
      class="director-section surface-panel surface-panel--elevated">
      <div class="container">
        <h2 class="director__title">{{ schoolData?.parsedDirectorBlock.Title }}</h2>

        <div class="director__content">
          <div class="director__text">
            <UiPrepeersQlEditorContent :html="schoolData?.parsedDirectorBlock.Content ?? ''" />
          </div>

          <div class="director__profile">
            <!-- Photo circulaire -->
            <div class="director__photo-wrapper">
              <img
                :src="schoolData?.parsedDirectorBlock.PictUrl"
                :alt="
                  schoolData?.parsedDirectorBlock.FirstName +
                  ' ' +
                  schoolData?.parsedDirectorBlock.LastName
                "
                class="director__photo" />
            </div>

            <!-- Nom -->
            <h3 class="director__name">
              {{ schoolData?.parsedDirectorBlock.FirstName }}
              {{ schoolData?.parsedDirectorBlock.LastName }}
            </h3>

            <!-- Lien LinkedIn -->
            <a
              v-if="schoolData?.parsedDirectorBlock.LinkedInLink"
              :href="schoolData?.parsedDirectorBlock.LinkedInLink"
              target="_blank"
              rel="noopener noreferrer"
              class="director__linkedin"
              aria-label="Profil LinkedIn">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor">
                <path
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.475-2.236-1.986-2.236-1.081 0-1.722.722-2.004 1.418-.103.249-.129.597-.129.946v5.441h-3.554s.047-8.733 0-9.637h3.554v1.364c.429-.659 1.194-1.608 2.996-1.608 2.108 0 3.686 1.378 3.686 4.342v5.539zM5.337 8.855c-1.144 0-1.915-.759-1.915-1.71 0-.955.77-1.71 1.952-1.71 1.18 0 1.914.755 1.938 1.71 0 .951-.758 1.71-1.975 1.71zm1.581 11.597H3.819V9.815h3.099v10.637zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ── NOS CHIFFRES POST FORMATION ─────────────────────────────────────────────── -->
    <section
      v-if="hasStatsBlockContent"
      class="stats-section surface-panel surface-panel--elevated">
      <div class="container">
        <h2 class="stats__title">{{ schoolData?.parsedStatsBlock.Employment.Title }}</h2>
        <p class="stats__subtitle">{{ schoolData?.parsedStatsBlock.Employment.SubTitle }}</p>

        <!-- DONUT + SALAIRES -->
        <div
          v-if="showStatsTopRow"
          class="stats__top-row"
          :class="{
            'stats__top-row--donut-only': showDonut && !hasSalaryData,
            'stats__top-row--salaries-only': !showDonut && hasSalaryData,
          }">
          <!-- DONUT -->
          <div
            v-if="showDonut"
            class="stats__donut-wrapper">
            <svg
              viewBox="0 0 42 42"
              class="stats__donut">
              <defs>
                <linearGradient
                  id="linear-gradient-donut"
                  x1="1"
                  x2="0"
                  y2="1"
                  gradientUnits="objectBoundingBox">
                  <stop
                    offset="0"
                    stop-color="#ffc300" />
                  <stop
                    offset="1"
                    stop-color="#ff5100" />
                </linearGradient>
              </defs>
              <!-- Fond gris -->
              <circle
                cx="21"
                cy="21"
                r="15.91549430918954"
                fill="transparent"
                stroke="#5a4a3a"
                stroke-width="6" />
              <!-- Segment orange -->
              <circle
                cx="21"
                cy="21"
                r="15.91549430918954"
                fill="transparent"
                stroke="url(#linear-gradient-donut)"
                stroke-width="7"
                :stroke-dasharray="`${employmentRate} ${100 - employmentRate}`"
                stroke-dashoffset="0"
                stroke-linecap="round" />
              <!-- Trou central -->
              <circle
                cx="21"
                cy="21"
                r="10.91549430918954"
                fill="#1a1a2e" />
            </svg>
            <div class="stats__donut-text">
              <div class="stats__donut-value">{{ employmentRate }}%</div>
              <div class="stats__donut-label">Taux net d'emploi</div>
            </div>
          </div>

          <!-- SALAIRES -->
          <div
            v-if="hasSalaryData"
            class="stats__salaries">
            <h3 class="stats__salaries-title">Les rémunérations moyennes</h3>

            <!-- FRANCE -->
            <div class="stats__salary-country">
              <h4 class="stats__salary-country-title">France</h4>

              <div class="stats__salary-item">
                <div class="stats__salary-label">Salaire annuel brut hors primes :</div>
                <div class="stats__salary-value">
                  Entre {{ schoolData?.parsedStatsBlock.Employment.SalMinFR }} et
                  {{ schoolData?.parsedStatsBlock.Employment.SalMaxFR }}
                </div>
              </div>

              <div class="stats__salary-item">
                <div class="stats__salary-label">
                  Salaire annuel brut avec primes et avantages :
                </div>
                <div class="stats__salary-value">
                  Entre {{ schoolData?.parsedStatsBlock.Employment.SalMinWithPFR }} et
                  {{ schoolData?.parsedStatsBlock.Employment.SalMaxWithPFR }}
                </div>
              </div>
            </div>

            <!-- ÉTRANGER -->
            <div class="stats__salary-country">
              <h4 class="stats__salary-country-title">Étranger</h4>

              <div class="stats__salary-item">
                <div class="stats__salary-label">Salaire annuel brut hors primes :</div>
                <div class="stats__salary-value">
                  Entre {{ schoolData?.parsedStatsBlock.Employment.SalMinAB }} et
                  {{ schoolData?.parsedStatsBlock.Employment.SalMaxAB }}
                </div>
              </div>

              <div class="stats__salary-item">
                <div class="stats__salary-label">
                  Salaire annuel brut avec primes et avantages :
                </div>
                <div class="stats__salary-value">
                  Entre {{ schoolData?.parsedStatsBlock.Employment.SalMinWithPAB }} et
                  {{ schoolData?.parsedStatsBlock.Employment.SalMaxWithPAB }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-for="stat in statsSectionsWithLinks"
          :key="stat.Title"
          class="stats__bottom-section">
          <h3 class="stats__title">{{ stat.Title }}</h3>
          <div class="stats__wrapper">
            <NuxtLink
              v-for="link in stat.Links"
              :key="link.Url"
              :to="link.Url"
              class="stats__tag">
              {{ link.Text }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- ── NOS CAMPUS ─────────────────────────────────────────────── -->
    <AcaCampusCarousel :school-data="schoolData" />

    <!-- ── LIENS PARTNERS ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.parsedPartners.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ t('seointer.root.title_partners') }}</h2>
        <p class="media-grid__desc">{{ t('seointer.root.description_partners') }}</p>
        <div class="media-grid__cards">
          <UiSchoolCard
            v-for="school in schoolData?.parsedPartners"
            :key="school.id"
            :school="school" />
        </div>
      </div>
    </section>

    <!-- ── LIENS PROGRAMS ─────────────────────────────────────────────── -->
    <!-- <section v-if="schoolData?.Programs.length" class="programs-section">
      <div class="container">
        <h2 class="programs__title">{{ t('seointer.root.title_programs') }}</h2>
        <p class="programs__desc">{{ t('seointer.root.description_programs') }}</p>
        <div class="programs__cards">
          <UiSchoolCard
            v-for="program in schoolData?.Programs"
            :key="program.id"
            :school="program"
          />
        </div>
      </div>
    </section>     -->

    <!-- ── LIENS ARTICLES BLOG ─────────────────────────────────────────────── -->
    <section
      v-if="schoolData?.parsedArticles.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ t('seointer.root.title_articles') }}</h2>
        <p class="media-grid__desc">{{ t('seointer.root.description_articles') }}</p>
        <div class="media-grid__cards">
          <UiArticleCard
            v-for="article in schoolData?.parsedArticles"
            :key="article.id"
            :article="article" />
        </div>
      </div>
    </section>
  </template>
</template>

<script setup lang="ts">
import SchoolDetailSkeleton from '~/components/aca/SchoolDetailSkeleton.vue'
import PrepeersEntityLogo from '~/components/core/PrepeersEntityLogo.vue'
import PrepeersHeroCover from '~/components/core/PrepeersHeroCover.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import { computed } from 'vue'
import type { ComputedRef, PropType } from 'vue'
import type { SchoolDataResponse, SeoSiblingsBlock } from '~/types/school'
import { filterSeoLinksWithLabel, formatSeoIntro } from '~/utils/helpers'
import PrePeersLolaCta from '~/components/ui/PrePeersLolaCta.vue'
import PrePeersHighlightsSection from '~/components/sections/PrePeersHighlightsSection.vue'

interface SchoolDetailProps {
  schoolData: SchoolDataResponse | null
  loading?: boolean
  isLoggedIn: boolean
}

const props: SchoolDetailProps = defineProps({
  schoolData: {
    type: Object as PropType<SchoolDataResponse | null>,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isLoggedIn: {
    type: Boolean,
    default: false,
  },
})

const localePath = useLocalePath()
const { t } = useI18n()

/**
 * @param {string | null | undefined} value - Montant brut renvoyé par l'API.
 * @returns {boolean} true si la valeur est affichable (non vide, non « 0 »).
 */
function isMeaningfulSalaryValue(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim()
  return trimmed.length > 0 && trimmed !== '0'
}

/**
 * @param {string} min - Borne basse.
 * @param {string} max - Borne haute.
 * @returns {boolean} true si les deux bornes sont exploitables.
 */
function hasSalaryRange(min: string, max: string): boolean {
  return isMeaningfulSalaryValue(min) && isMeaningfulSalaryValue(max)
}

const employmentRate: ComputedRef<number> = computed<number>(() => {
  const raw = props.schoolData?.parsedStatsBlock?.Employment?.EmploymentRate
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : 0
})

const showDonut: ComputedRef<boolean> = computed<boolean>(() => employmentRate.value > 0)

const hasSalaryData: ComputedRef<boolean> = computed<boolean>(() => {
  const employment = props.schoolData?.parsedStatsBlock?.Employment
  if (!employment) return false

  const hasFrenchSalary =
    hasSalaryRange(employment.SalMinFR, employment.SalMaxFR) ||
    hasSalaryRange(employment.SalMinWithPFR, employment.SalMaxWithPFR)
  const hasAbroadSalary =
    hasSalaryRange(employment.SalMinAB, employment.SalMaxAB) ||
    hasSalaryRange(employment.SalMinWithPAB, employment.SalMaxWithPAB)

  return hasFrenchSalary || hasAbroadSalary
})

const showStatsTopRow: ComputedRef<boolean> = computed<boolean>(
  () => showDonut.value || hasSalaryData.value,
)

/** Blocs « débouchés » avec au moins un lien affichable. */
const statsSectionsWithLinks: ComputedRef<SeoSiblingsBlock[]> = computed<SeoSiblingsBlock[]>(() => {
  const stats = props.schoolData?.parsedStatsBlock?.Stats ?? []
  return stats
    .map((stat) => ({
      ...stat,
      Links: filterSeoLinksWithLabel(stat.Links),
    }))
    .filter((stat) => stat.Links.length > 0)
})

/**
 * Affiche la section si taux d'emploi, salaires ou tags secteurs sont renseignés.
 * Sans ça, `parsedStatsBlock` existe toujours (fallback `EMPTY_STATS_BLOCK` côté parse).
 */
const hasStatsBlockContent: ComputedRef<boolean> = computed<boolean>(
  () => showDonut.value || hasSalaryData.value || statsSectionsWithLinks.value.length > 0,
)
</script>

<style scoped>
.btn-medium {
  background-color: var(--accent-blue);
  border: solid 1px var(--accent-blue);
  color: white;
  font-size: var(--text-md);
}

/* ── LE MOT DU DIRECTEUR ─────────────────────────────────────────────── */
.director__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-12);
  text-align: left;
}

.director__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-12);
}

.director__text {
  width: 100%;
}

.director__text :deep(.prepeers-ql-editor-content) {
  margin: 0;
}

.director__profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-6);
}

/* Photo circulaire */
.director__photo-wrapper {
  position: relative;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
}

.director__photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

/* Nom */
.director__name {
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: var(--tracking-wide);
}

/* Lien LinkedIn */
.director__linkedin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background-color: white;
  color: #0a66c2;
  border-radius: var(--radius-md);
  transition: all var(--duration-fast) var(--ease-out);
  text-decoration: none;
}

.director__linkedin:hover {
  background-color: #f0f0f0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(10, 102, 194, 0.2);
}

.director__linkedin svg {
  width: 24px;
  height: 24px;
}

/* ── NOS CHIFFRES POST FORMATION ─────────────────────────────────────────────── */
.stats__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-2);
  text-align: left;
}

.stats__subtitle {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-bottom: var(--space-12);
  font-style: italic;
}

/* ── DONUT + SALAIRES ─────────────────────────────────────────────── */
.stats__top-row {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: var(--space-12);
  margin-bottom: var(--space-1);
  align-items: start;
}

.stats__top-row--donut-only {
  grid-template-columns: 1fr;
  justify-items: center;
}

.stats__top-row--donut-only .stats__donut-wrapper {
  width: 100%;
  max-width: 360px;
  margin-inline: auto;
}

.stats__top-row--salaries-only {
  grid-template-columns: 1fr;
}

.stats__donut-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-8);
  background-color: var(--bg-elevated);
  border-radius: var(--radius-lg);
  text-align: center;
}

.stats__donut {
  width: 100%;
  max-width: 280px;
  height: auto;
  aspect-ratio: 1;
  margin-bottom: var(--space-8);
}

.stats__donut-text {
  width: 100%;
}

.stats__donut-value {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 800;
  color: white;
  line-height: 1;
  margin-bottom: var(--space-2);
  letter-spacing: var(--tracking-tight);
}

.stats__donut-label {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

/* ── SALAIRES ─────────────────────────────────────────────── */
.stats__salaries {
  background-color: rgba(0, 0, 0, 0.2);
  padding: var(--space-8);
  border-radius: var(--radius-lg);
}

.stats__salaries-title {
  font-size: var(--text-lg);
  font-weight: 700;
  color: #ffc300;
  margin-bottom: var(--space-8);
  text-align: left;
}

.stats__salary-country {
  margin-bottom: var(--space-8);
}

.stats__salary-country:last-child {
  margin-bottom: 0;
}

.stats__salary-country-title {
  font-size: var(--text-md);
  font-weight: 700;
  color: #ffc300;
  margin-bottom: var(--space-4);
  text-align: left;
}

.stats__salary-item {
  margin-bottom: var(--space-4);
}

.stats__salary-item:last-child {
  margin-bottom: 0;
}

.stats__salary-label {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  margin-bottom: var(--space-1);
}

.stats__salary-value {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.8);
  padding-left: var(--space-4);
  border-left: 2px solid rgba(255, 195, 0, 0.3);
}

/* ── SECTEURS PROFESSIONNELS ─────────────────────────────────────────────── */
.stats__bottom-section {
  padding-top: var(--space-8);
  /* border-top: 1px solid rgba(255, 255, 255, 0.1); */
}

.stats__title {
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-8);
  text-align: left;
}

.stats__wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  justify-content: center;
}

.stats__group {
  display: contents;
}

.stats__tag {
  display: inline-block;
  padding: var(--space-3) var(--space-6);
  background-color: rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md);
  text-decoration: none;
  font-size: var(--text-sm);
  font-weight: 500;
  transition: all var(--duration-fast) var(--ease-out);
}

.stats__:hover {
  background-color: rgba(255, 195, 0, 0.2);
  border-color: rgba(255, 195, 0, 0.5);
  color: #ffc300;
}

/* ── LEAFLET OVERRIDES ─────────────────────────────────────────────── */
:deep(.leaflet-control-attribution) {
  background-color: rgba(255, 255, 255, 0.8) !important;
  font-size: 11px !important;
}

:deep(.leaflet-marker-icon) {
  filter: hue-rotate(0deg) brightness(1.1);
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .stats__top-row {
    grid-template-columns: 1fr;
    gap: var(--space-8);
  }

  .stats__salaries {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-6);
  }

  .stats__salaries-title {
    grid-column: 1 / -1;
  }

  .stats__salary-country {
    background-color: rgba(0, 0, 0, 0.3);
    padding: var(--space-6);
    border-radius: var(--radius-md);
    margin-bottom: 0;
  }
}

@media (max-width: 768px) {
  .director__title {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
  }

  .director__content {
    gap: var(--space-8);
  }

  .director__text {
    max-width: 600px;
  }

  .director__photo-wrapper {
    width: 120px;
    height: 120px;
  }

  .director__name {
    font-size: var(--text-lg);
  }

  .director__linkedin {
    width: 40px;
    height: 40px;
  }

  .director__linkedin svg {
    width: 20px;
    height: 20px;
  }

  .stats__title {
    font-size: var(--text-xl);
  }

  .stats__top-row {
    gap: var(--space-6);
    margin-bottom: var(--space-12);
  }

  .stats__donut {
    max-width: 240px;
  }

  .stats__donut-value {
    font-size: var(--text-4xl);
  }

  .stats__donut-label {
    font-size: var(--text-xs);
  }

  .stats__salaries {
    display: block;
  }

  .stats__salary-country {
    margin-bottom: var(--space-6);
  }

  .stats__salary-country:last-child {
    margin-bottom: 0;
  }

  .stats__salary-country-title {
    font-size: var(--text-base);
  }

  .stats__salary-label {
    font-size: var(--text-xs);
  }

  .stats__salary-value {
    font-size: var(--text-xs);
  }

  .stats__sectors-title {
    font-size: var(--text-lg);
  }

  .stats__sector-tag {
    font-size: var(--text-xs);
    padding: var(--space-2) var(--space-4);
  }
}

@media (max-width: 640px) {
  .director__title {
    font-size: var(--text-lg);
    margin-bottom: var(--space-6);
  }

  .director__content {
    gap: var(--space-6);
  }

  .director__photo-wrapper {
    width: 100px;
    height: 100px;
    border-width: 2px;
  }

  .director__name {
    font-size: var(--text-base);
  }

  .director__linkedin {
    width: 36px;
    height: 36px;
  }

  .director__linkedin svg {
    width: 18px;
    height: 18px;
  }

  .stats__title {
    font-size: var(--text-lg);
  }

  .stats__subtitle {
    font-size: var(--text-xs);
  }

  .stats__donut {
    max-width: 200px;
  }

  .stats__donut-value {
    font-size: var(--text-3xl);
  }

  .stats__salaries {
    padding: var(--space-6);
  }

  .stats__salary-country {
    margin-bottom: var(--space-4);
  }

  .stats__salary-label {
    font-size: var(--text-xs);
  }

  .stats__salary-value {
    font-size: var(--text-xs);
  }

  .stats__sectors-title {
    font-size: var(--text-base);
  }

  .stats__sector-tag {
    font-size: var(--text-xs);
  }

  .stats__bottom-section {
    margin-top: var(--space-8);
    padding-top: var(--space-6);
  }
}
</style>
